import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { useAuth } from "@/context/AuthContext";
import Splash from "@/pages/Splash";
import Login from "@/pages/Login";
import Landing from "@/pages/public/Landing";
import About from "@/pages/public/About";
import AboutBiteGoFood from "@/pages/public/AboutBiteGoFood";
import Faq from "@/pages/public/Faq";
import Contact from "@/pages/public/Contact";
import AuthCallback from "@/pages/auth/AuthCallback";
import CustomerSignup from "@/pages/auth/CustomerSignup";
import RestaurantOnboarding from "@/pages/partner/RestaurantOnboarding";
import DeliveryOnboarding from "@/pages/partner/DeliveryOnboarding";
import Home from "@/pages/customer/Home";
import SearchPage from "@/pages/customer/Search";
import RestaurantDetail from "@/pages/customer/RestaurantDetail";
import Cart from "@/pages/customer/Cart";
import Checkout from "@/pages/customer/Checkout";
import OrderTracking from "@/pages/customer/OrderTracking";
import Orders from "@/pages/customer/Orders";
import Profile from "@/pages/customer/Profile";
import Favorites from "@/pages/customer/Favorites";
import Offers from "@/pages/customer/Offers";
import Rewards from "@/pages/customer/Rewards";
import Support from "@/pages/customer/Support";
import Addresses from "@/pages/customer/Addresses";
import Notifications from "@/pages/customer/Notifications";
import Quick from "@/pages/customer/Quick";
import DineOut from "@/pages/customer/DineOut";
import LateNight from "@/pages/customer/LateNight";
import ValueEats from "@/pages/customer/ValueEats";
import FoodOnTrain from "@/pages/customer/FoodOnTrain";
import HealthyMode from "@/pages/customer/HealthyMode";
import Cinema from "@/pages/customer/Cinema";
import MealPlans from "@/pages/customer/MealPlans";
import RestaurantDashboard from "@/pages/restaurant/RestaurantDashboard";
import DeliveryDashboard from "@/pages/delivery/DeliveryDashboard";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminLogin from "@/pages/admin/AdminLogin";
import { PrivacyPolicy, Terms } from "@/pages/legal/LegalPages";

function Protected({ role, children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash silent />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    const map = { customer: "/", restaurant: "/partner", delivery: "/rider", admin: "/admin" };
    return <Navigate to={map[user.role] || "/login"} replace />;
  }
  return children;
}

// Gate for the discreet admin entry point: only unauthenticated visitors (prospective
// admins) see the admin auth page. Authenticated non-admins are sent to their own area
// (never shown the admin flow); authenticated admins skip straight to the dashboard.
function AdminLoginGate() {
  const { user, loading } = useAuth();
  if (loading) return <Splash silent />;
  if (user) {
    if (user.role === "admin") return <Navigate to="/admin" replace />;
    const map = { customer: "/", restaurant: "/partner", delivery: "/rider" };
    return <Navigate to={map[user.role] || "/"} replace />;
  }
  return <AdminLogin />;
}

// Root URL: public, crawlable BiteGoFood landing page for logged-out visitors;
// authenticated users continue straight into their existing app experience.
function RootRoute() {
  const { user, loading } = useAuth();
  if (loading) return <Splash silent />;
  if (!user) return <Landing />;
  if (user.role === "customer") return <Home />;
  const map = { restaurant: "/partner", delivery: "/rider", admin: "/admin" };
  return <Navigate to={map[user.role] || "/login"} replace />;
}

function App() {
  return (
    <HelmetProvider>
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<AdminLoginGate />} />
          <Route path="/splash" element={<Splash />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/signup" element={<CustomerSignup />} />
          <Route path="/about" element={<About />} />
          <Route path="/about/bitegofood" element={<AboutBiteGoFood />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/partner/register" element={<RestaurantOnboarding />} />
          <Route path="/rider/register" element={<DeliveryOnboarding />} />

          <Route path="/" element={<RootRoute />} />
          <Route path="/search" element={<Protected role="customer"><SearchPage /></Protected>} />
          <Route path="/restaurant/:id" element={<Protected role="customer"><RestaurantDetail /></Protected>} />
          <Route path="/cart" element={<Protected role="customer"><Cart /></Protected>} />
          <Route path="/checkout" element={<Protected role="customer"><Checkout /></Protected>} />
          <Route path="/order/:id" element={<Protected role="customer"><OrderTracking /></Protected>} />
          <Route path="/orders" element={<Protected role="customer"><Orders /></Protected>} />
          <Route path="/profile" element={<Protected role="customer"><Profile /></Protected>} />
          <Route path="/favorites" element={<Protected role="customer"><Favorites /></Protected>} />
          <Route path="/offers" element={<Protected role="customer"><Offers /></Protected>} />
          <Route path="/rewards" element={<Protected role="customer"><Rewards /></Protected>} />
          <Route path="/support" element={<Protected role="customer"><Support /></Protected>} />
          <Route path="/addresses" element={<Protected role="customer"><Addresses /></Protected>} />
          <Route path="/notifications" element={<Protected role="customer"><Notifications /></Protected>} />
          <Route path="/quick" element={<Protected role="customer"><Quick /></Protected>} />
          <Route path="/dineout" element={<Protected role="customer"><DineOut /></Protected>} />
          <Route path="/late-night" element={<Protected role="customer"><LateNight /></Protected>} />
          <Route path="/value-eats" element={<Protected role="customer"><ValueEats /></Protected>} />
          <Route path="/train" element={<Protected role="customer"><FoodOnTrain /></Protected>} />
          <Route path="/healthy" element={<Protected role="customer"><HealthyMode /></Protected>} />
          <Route path="/cinema" element={<Protected role="customer"><Cinema /></Protected>} />
          <Route path="/meals" element={<Protected role="customer"><MealPlans /></Protected>} />

          <Route path="/partner/*" element={<Protected role="restaurant"><RestaurantDashboard /></Protected>} />
          <Route path="/rider/*" element={<Protected role="delivery"><DeliveryDashboard /></Protected>} />
          <Route path="/admin/*" element={<Protected role="admin"><AdminDashboard /></Protected>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </div>
    </HelmetProvider>
  );
}

export default App;
